import { createSession, createAnswer, createUpload, SESSION_STATUS, QUESTION_CATEGORIES, ARTIFACT_PHASES, ARTIFACT_TYPES } from '../types/session.js';
import taxonomyService from './taxonomyService.js';

// Mock API service - will be replaced with actual backend calls
class SessionService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
    this.sessions = new Map(); // Mock storage
  }

  // Create new session
  async createSession(userId, projectName, initialIdea) {
    console.log('🎆 Creating session:', { userId, projectName, initialIdea });
    
    // Apply ProphetPlay.ai defaults if missing
    const finalProjectName = projectName || 'ProphetPlay.ai';
    const finalInitialIdea = initialIdea || 'A high-volume, real-time prediction platform for sports, entertainment, and financial events without real money wagering.';
    
    const session = createSession(userId, finalProjectName);
    session.initialIdea = finalInitialIdea;
    session.status = SESSION_STATUS.GATHERING;
    
    // Mock API call
    this.sessions.set(session.sessionId, session);
    
    // Generate initial questions only once (idempotent)
    if (!session.questions || session.questions.length === 0) {
      session.questions = await this.generateInitialQuestions(finalInitialIdea);
      session.metadata.totalQuestions = session.questions.length;
    }
    
    console.log('✅ Session created:', {
      sessionId: session.sessionId,
      questionsCount: session.questions.length,
      firstQuestion: session.questions[0]?.text
    });
    
    return session;
  }

  // Get session by ID
  async getSession(sessionId) {
    return this.sessions.get(sessionId) || null;
  }

  // Submit answer to question
  async submitAnswer(sessionId, questionId, value, metadata = {}) {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error('Session not found');

    const answer = createAnswer(questionId, value, metadata);
    session.answers[questionId] = answer;
    session.updatedAt = new Date().toISOString();
    
    // Mark question as answered
    const question = session.questions.find(q => q.id === questionId);
    if (question) {
      question.answered = true;
      question.answer = value;
    }

    // Update progress
    session.metadata.completedQuestions = Object.keys(session.answers).length;
    session.currentQuestionIndex = Math.min(
      session.currentQuestionIndex + 1, 
      session.questions.length - 1
    );

    // Disable follow-up questions to prevent duplication
    // Keep questions stable and predictable

    return session;
  }

  // Upload file
  async uploadFile(sessionId, file, type = 'document') {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error('Session not found');

    const upload = createUpload(file, type);
    session.uploads.push(upload);
    session.updatedAt = new Date().toISOString();

    // Mock file processing
    setTimeout(() => {
      upload.processed = true;
      upload.extractedContent = `Extracted content from ${file.name}`;
    }, 2000);

    return upload;
  }

  // Generate requirements document
  async generateRequirements(sessionId) {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error('Session not found');

    session.status = SESSION_STATUS.REVIEWING;
    
    // Generate structured requirements
    session.requirements = {
      id: crypto.randomUUID(),
      sessionId,
      projectName: session.projectName,
      initialIdea: session.initialIdea,
      
      business: this.extractBusinessRequirements(session.answers),
      security: this.extractSecurityRequirements(session.answers),
      reliability: { rto: '4 hours', rpo: '1 hour', availabilityZones: 'multi-az' },
      performance: { readWriteMix: '80/20', cachingNeeds: ['Product catalog', 'User sessions'] },
      integrations: this.extractIntegrationRequirements(session.answers),
      data: this.extractDataRequirements(session.answers),
      operations: this.extractOperationsRequirements(session.answers),
      
      unlockedPhases: this.determineUnlockedPhases(session.answers),
      confidence: this.calculateConfidence(session.answers),
      gaps: this.identifyGaps(session.answers),
      generatedAt: new Date().toISOString()
    };
    
    // Update session metadata
    session.metadata.unlockedArtifactPhases = session.requirements.unlockedPhases;

    return session.requirements;
  }

  // Generate artifacts
  async generateArtifacts(sessionId) {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error('Session not found');

    session.status = SESSION_STATUS.GENERATING;
    
    // Generate artifacts based on unlocked phases
    const artifacts = [];
    const unlockedPhases = session.requirements?.unlockedPhases || [ARTIFACT_PHASES.FOUNDATION];
    
    // Foundation artifacts (always generated)
    if (unlockedPhases.includes(ARTIFACT_PHASES.FOUNDATION)) {
      const foundationArtifacts = taxonomyService.getCoreArtifacts()
        .filter(artifact => artifact.lifecycle_stage.includes('Design'))
        .slice(0, 3)
        .map(artifact => ({
          id: crypto.randomUUID(),
          type: artifact.acronym,
          title: artifact.name,
          description: artifact.meaning,
          status: 'ready',
          phase: ARTIFACT_PHASES.FOUNDATION,
          format: artifact.typical_format.split(',')[0],
          downloadUrl: `/mock/${artifact.acronym.toLowerCase()}.${this.getFileExtension(artifact.typical_format)}`,
          previewUrl: `/mock/${artifact.acronym.toLowerCase()}-preview.png`
        }));
      artifacts.push(...foundationArtifacts);
    }
    
    // Data model artifacts
    if (unlockedPhases.includes(ARTIFACT_PHASES.DATA_MODEL)) {
      artifacts.push({
        id: crypto.randomUUID(),
        type: ARTIFACT_TYPES.ERD,
        title: 'Entity Relationship Diagram',
        description: 'Core data entities and relationships',
        status: 'ready',
        phase: ARTIFACT_PHASES.DATA_MODEL,
        downloadUrl: '/mock/erd.svg',
        previewUrl: '/mock/erd-preview.png'
      });
    }
    
    // API design artifacts
    if (unlockedPhases.includes(ARTIFACT_PHASES.API_DESIGN)) {
      artifacts.push(
        {
          id: crypto.randomUUID(),
          type: ARTIFACT_TYPES.SEQUENCE_DIAGRAM,
          title: 'API Sequence Diagrams',
          description: 'Key user journey API interactions',
          status: 'ready',
          phase: ARTIFACT_PHASES.API_DESIGN,
          downloadUrl: '/mock/sequence.svg',
          previewUrl: '/mock/sequence-preview.png'
        },
        {
          id: crypto.randomUUID(),
          type: ARTIFACT_TYPES.OPENAPI_SPEC,
          title: 'OpenAPI Specification',
          description: 'REST API documentation and contracts',
          status: 'ready',
          phase: ARTIFACT_PHASES.API_DESIGN,
          downloadUrl: '/mock/openapi.yaml',
          previewUrl: '/mock/openapi-preview.png'
        }
      );
    }
    
    // Deployment artifacts
    if (unlockedPhases.includes(ARTIFACT_PHASES.DEPLOYMENT)) {
      artifacts.push(
        {
          id: crypto.randomUUID(),
          type: ARTIFACT_TYPES.DEPLOYMENT_DIAGRAM,
          title: 'AWS Deployment Architecture',
          description: 'Cloud infrastructure and deployment topology',
          status: 'ready',
          phase: ARTIFACT_PHASES.DEPLOYMENT,
          downloadUrl: '/mock/deployment.svg',
          previewUrl: '/mock/deployment-preview.png'
        },
        {
          id: crypto.randomUUID(),
          type: ARTIFACT_TYPES.INFRASTRUCTURE_CODE,
          title: 'Infrastructure as Code',
          description: 'Terraform/CDK templates for AWS deployment',
          status: 'ready',
          phase: ARTIFACT_PHASES.DEPLOYMENT,
          downloadUrl: '/mock/infrastructure.zip',
          previewUrl: '/mock/infrastructure-preview.png'
        }
      );
    }

    session.artifacts = artifacts;
    session.status = SESSION_STATUS.COMPLETE;
    
    return artifacts;
  }

  // Helper methods for requirements analysis
  calculateConfidence(answers) {
    const totalQuestions = Object.keys(answers).length;
    const requiredAnswered = Object.values(answers).filter(a => a.value).length;
    return Math.round((requiredAnswered / Math.max(totalQuestions, 1)) * 100);
  }

  identifyGaps(answers) {
    const gaps = [];
    
    if (!answers['data-1']?.value) {
      gaps.push('Core data entities not fully defined');
    }
    
    if (!answers['integrations-1']?.value) {
      gaps.push('Payment integration requirements unclear');
    }
    
    if (!answers['operations-1']?.value) {
      gaps.push('Deployment strategy not specified');
    }
    
    return gaps;
  }

  // Educational, scale-aware questions for comprehensive architecture design
  async generateInitialQuestions(initialIdea) {
    const questions = [];
    
    // Phase 1: Business Context & Scale Assessment
    questions.push(
      {
        id: 'business-model',
        category: QUESTION_CATEGORIES.BUSINESS,
        text: 'What business models apply to your system? (Select up to 3)',
        explanation: 'Many systems combine multiple business models. ProphetPlay.ai is Gaming/Entertainment + SaaS Platform + Content/Media. E-commerce handles transactions and inventory, Software as a Service (SaaS) focuses on subscriptions and user management, Gaming platforms need real-time interactions and leaderboards.',
        options: [
          'E-commerce/Marketplace (buying/selling products)',
          'Software as a Service (SaaS) Platform', 
          'Gaming/Entertainment (real-time interactions)',
          'Financial Services (payments, trading, banking)',
          'Content/Media (publishing, streaming)',
          'Enterprise Tools (business productivity)',
          'Other/Custom'
        ],
        type: 'multi-select',
        maxSelections: 3,
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.FOUNDATION]
      },
      {
        id: 'scale-tier',
        category: QUESTION_CATEGORIES.BUSINESS,
        text: 'What is your expected scale and risk profile?',
        explanation: 'This determines your architecture complexity tier. Starter Pack: simple applications with basic requirements. Serious Product: handles sensitive data like payments or Personally Identifiable Information (PII). Enterprise: highly regulated with strict compliance needs.',
        options: [
          'Starter Pack (< 10K users, basic features, low risk)',
          'Serious Product (10K-100K users, payments/PII, moderate risk)', 
          'Enterprise Scale (100K+ users, highly regulated, high risk)',
          'Real-time/High-frequency (gaming, trading, live events)',
          'Custom/Unsure'
        ],
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.FOUNDATION]
      },
      {
        id: 'availability-slo',
        category: QUESTION_CATEGORIES.PERFORMANCE,
        text: 'What are your availability and performance Service Level Objectives (SLOs)?',
        explanation: 'Service Level Objectives (SLOs) are measurable targets for system performance. 99.9% availability means ~8.7 hours downtime per year. Response time is how fast your system responds to user requests. Choose based on user expectations and business impact.',
        options: [
          'Standard (99.9% uptime, < 1 second response)',
          'High Performance (99.95% uptime, < 500ms response)',
          'Mission Critical (99.99% uptime, < 200ms response)',
          'Real-time (99.99%+ uptime, < 100ms response)',
          'Custom requirements',
          'Not sure/Help me decide'
        ],
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.FOUNDATION]
      },
      {
        id: 'compliance-requirements',
        category: QUESTION_CATEGORIES.SECURITY,
        text: 'What compliance and regulatory requirements apply to your system?',
        explanation: 'Compliance requirements significantly impact architecture design. Payment Card Industry Data Security Standard (PCI DSS) for payments, General Data Protection Regulation (GDPR) for EU users, Health Insurance Portability and Accountability Act (HIPAA) for healthcare, Sarbanes-Oxley Act (SOX) for public companies.',
        options: [
          'None (public data, low regulation)',
          'GDPR (EU users, data privacy)',
          'PCI DSS (payment card processing)',
          'HIPAA (healthcare data)',
          'SOX/Financial regulations',
          'Multiple compliance requirements',
          'Not sure what applies'
        ],
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.FOUNDATION]
      }
    );
    
    // Phase 2: Security & Data Classification
    questions.push(
      {
        id: 'data-sensitivity',
        category: QUESTION_CATEGORIES.SECURITY,
        text: 'What types of sensitive data will your system handle?',
        explanation: 'Data classification drives security architecture. Payment data requires PCI DSS compliance and encryption. PII needs privacy controls and access logging. Financial data requires audit trails and segregation of duties. This determines encryption, access controls, and monitoring requirements.',
        options: [
          'Public data only (no sensitive information)',
          'User profiles and preferences (basic PII)',
          'Payment and financial information',
          'Healthcare or biometric data',
          'Proprietary business data and trade secrets',
          'Multiple sensitive data types',
          'Not sure/Need guidance'
        ],
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.FOUNDATION]
      },
      {
        id: 'authentication-model',
        category: QUESTION_CATEGORIES.SECURITY,
        text: 'What authentication and authorization model do you need?',
        explanation: 'Authentication (AuthN) verifies who users are, Authorization (AuthZ) controls what they can do. Social login is convenient but limited. Multi-Factor Authentication (MFA) adds security. Single Sign-On (SSO) integrates with corporate systems. Role-Based Access Control (RBAC) manages permissions.',
        options: [
          'Social login only (Google, Facebook, Apple)',
          'Email/password with social options',
          'Enterprise SSO (SAML, OIDC integration)',
          'Multi-Factor Authentication (MFA) required',
          'Anonymous/guest access supported',
          'Complex role-based permissions',
          'Not applicable'
        ],
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.FOUNDATION]
      }
    );
    
    // Phase 3: Data Architecture & Integration
    questions.push(
      {
        id: 'data-model',
        category: QUESTION_CATEGORIES.DATA,
        text: 'What are your core data entities and their relationships?',
        explanation: 'Data entities are the main "things" your system manages. E-commerce has Products, Orders, Customers. Social platforms have Users, Posts, Comments. Gaming has Players, Games, Scores. Understanding relationships helps design databases and Application Programming Interfaces (APIs).',
        options: [
          'Simple: Users + Content (blogs, social media)',
          'E-commerce: Users + Products + Orders + Inventory',
          'Gaming: Users + Games + Scores + Leaderboards',
          'Financial: Users + Accounts + Transactions + Reports',
          'Complex multi-entity business model',
          'Event-driven data (real-time streams)',
          'Custom/Need help defining'
        ],
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.DATA_MODEL]
      },
      {
        id: 'external-integrations',
        category: QUESTION_CATEGORIES.INTEGRATIONS,
        text: 'What external systems and APIs do you need to integrate with? (Select all that apply)',
        explanation: 'External integrations add complexity and dependencies. ProphetPlay.ai would likely need: Payment processors, Social media APIs, Real-time data feeds, and Analytics platforms. Each integration needs error handling, monitoring, and potentially an API Gateway for management.',
        options: [
          'Payment processors (Stripe, PayPal, Square)',
          'Social media APIs (Twitter, Facebook, Instagram)',
          'Real-time data feeds (sports, financial, news)',
          'Enterprise systems (ERP, CRM, HR systems)',
          'Analytics and monitoring platforms',
          'Email/SMS notification services',
          'Minimal/no external integrations'
        ],
        type: 'multi-select',
        maxSelections: 6,
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.API_DESIGN]
      }
    );
    
    // Phase 4: Infrastructure & Operations
    questions.push(
      {
        id: 'deployment-strategy',
        category: QUESTION_CATEGORIES.OPERATIONS,
        text: 'What cloud services and deployment patterns do you prefer? (Select up to 4)',
        explanation: 'Modern applications often use multiple deployment patterns. ProphetPlay.ai might use: Serverless for APIs, Containers for real-time services, Managed databases, and Content Delivery Networks (CDNs). You can mix AWS Lambda + Google Cloud + Azure services. Hybrid means some on-premises + cloud.',
        options: [
          'Serverless functions (Lambda, Cloud Functions)',
          'Container orchestration (ECS, EKS, Kubernetes)',
          'Traditional cloud servers (EC2, Compute Engine)',
          'Managed databases (RDS, DynamoDB, Firestore)',
          'Real-time services (Kinesis, Pub/Sub, Event Hubs)',
          'Content delivery (CloudFront, CDN)',
          'Hybrid (on-premises + cloud)',
          'Not sure/Need recommendations'
        ],
        type: 'multi-select',
        maxSelections: 4,
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.DEPLOYMENT]
      },
      {
        id: 'operational-maturity',
        category: QUESTION_CATEGORIES.OPERATIONS,
        text: 'What is your team\'s operational maturity and requirements?',
        explanation: 'Operational maturity determines architecture complexity. Minimum Viable Product (MVP) focuses on speed to market. Growth stage needs scalability and reliability. Enterprise requires compliance, audit trails, and governance. This affects monitoring, Continuous Integration/Continuous Deployment (CI/CD), and operational procedures.',
        options: [
          'MVP/Startup (simple, fast deployment, minimal ops)',
          'Growth stage (scalable, reliable, basic monitoring)',
          'Enterprise (compliant, auditable, full governance)',
          'High-scale (performance critical, advanced ops)',
          'Regulated environment (banking, healthcare)',
          'Custom operational requirements'
        ],
        required: true,
        answered: false,
        unlocks: [ARTIFACT_PHASES.DEPLOYMENT]
      }
    );
    
    return questions;
  }

  async generateFollowUpQuestions(session, questionId, answer) {
    // Disable follow-up questions to prevent duplication and keep questions stable
    return [];
  }

  // Extract structured requirements from answers
  extractBusinessRequirements(answers) {
    return {
      problemStatement: 'E-commerce platform for scalable online retail',
      productTypes: this.getAnswerValue(answers, 'business-1', []),
      volumeMetrics: {
        dailyOrders: this.getAnswerValue(answers, 'business-2', ''),
        peakMultiplier: 3 // Assumption
      },
      targetRegions: this.getAnswerValue(answers, 'business-3', []),
      slas: {
        availability: this.getAnswerValue(answers, 'business-4', ''),
        latency: this.extractLatencyFromSLA(answers['business-4']?.value)
      },
      compliance: this.extractComplianceFromAnswers(answers)
    };
  }

  extractSecurityRequirements(answers) {
    return {
      identityProvider: this.getAnswerValue(answers, 'security-1', ''),
      tokenManagement: {
        ttl: '1 hour', // Default
        refreshEnabled: true
      },
      keyManagement: 'AWS KMS',
      dataClassification: this.extractDataClassification(answers)
    };
  }

  extractDataRequirements(answers) {
    return {
      coreEntities: this.parseCoreEntities(answers['data-1']?.value),
      relationships: this.inferEntityRelationships(answers['data-1']?.value),
      retention: {
        policy: this.getAnswerValue(answers, 'data-2', ''),
        archivalStrategy: 'Automated'
      },
      residency: this.getAnswerValue(answers, 'business-3', '')
    };
  }

  extractIntegrationRequirements(answers) {
    return {
      paymentGateways: this.getAnswerValue(answers, 'integrations-1', []),
      externalSystems: this.getAnswerValue(answers, 'integrations-2', []),
      notifications: ['Email', 'SMS'] // Default
    };
  }

  extractOperationsRequirements(answers) {
    return {
      deploymentStrategy: this.getAnswerValue(answers, 'operations-1', ''),
      observabilityStack: ['CloudWatch', 'X-Ray'], // Default for AWS
      budgetPosture: this.getAnswerValue(answers, 'operations-2', '')
    };
  }

  // Helper methods
  getAnswerValue(answers, questionId, defaultValue) {
    return answers[questionId]?.value || defaultValue;
  }

  extractLatencyFromSLA(slaValue) {
    if (!slaValue) return null;
    const match = slaValue.match(/(\d+)ms/);
    return match ? parseInt(match[1]) : null;
  }

  extractComplianceFromAnswers(answers) {
    const compliance = [];
    if (answers['security-2']?.value?.includes('PCI')) {
      compliance.push('PCI DSS');
    }
    return compliance;
  }

  extractDataClassification(answers) {
    const classifications = ['Public', 'Internal'];
    if (answers['security-2']?.value?.includes('PCI')) {
      classifications.push('Restricted');
    }
    return classifications;
  }

  parseCoreEntities(entityAnswer) {
    if (!entityAnswer) return [];
    
    const entityMap = {
      'Product, Order, Customer': ['Product', 'Order', 'Customer'],
      'Add Inventory management': ['Product', 'Order', 'Customer', 'Inventory'],
      'Add User reviews/ratings': ['Product', 'Order', 'Customer', 'Review', 'Rating'],
      'Add Recommendations': ['Product', 'Order', 'Customer', 'Recommendation'],
    };
    
    return entityMap[entityAnswer] || ['Product', 'Order', 'Customer'];
  }

  inferEntityRelationships(entityAnswer) {
    return [
      { from: 'Customer', to: 'Order', type: 'one-to-many' },
      { from: 'Order', to: 'Product', type: 'many-to-many' },
      { from: 'Product', to: 'Inventory', type: 'one-to-one' }
    ];
  }

  // Determine which artifact phases are unlocked
  determineUnlockedPhases(answers) {
    const phases = [ARTIFACT_PHASES.FOUNDATION]; // Always unlocked
    
    // Check if we have enough data questions answered for ERD
    const dataQuestions = ['data-1', 'data-2'];
    if (dataQuestions.some(q => answers[q]?.value)) {
      phases.push(ARTIFACT_PHASES.DATA_MODEL);
    }
    
    // Check if we have integration details for API design
    const integrationQuestions = ['integrations-1'];
    if (integrationQuestions.some(q => answers[q]?.value)) {
      phases.push(ARTIFACT_PHASES.API_DESIGN);
    }
    
    // Check if we have operations details for deployment
    const operationQuestions = ['operations-1', 'operations-2'];
    if (operationQuestions.every(q => answers[q]?.value)) {
      phases.push(ARTIFACT_PHASES.DEPLOYMENT);
    }
    
    return phases;
  }

  // Helper method to get file extension from format
  getFileExtension(format) {
    const formatMap = {
      'Markdown': 'md',
      'PlantUML': 'puml',
      'Mermaid': 'mmd',
      'OpenAPI YAML/JSON': 'yaml',
      'DOCX': 'docx',
      'PDF': 'pdf'
    };
    const firstFormat = format.split(',')[0];
    return formatMap[firstFormat] || 'txt';
  }
}

const sessionService = new SessionService();
export default sessionService;